import Customer from '@/classes/case/Customer'
import customerApi from '@/api/customer/customerApi'
import FormData from '@/classes/form/FormData'
import FormDescriptor from '@/classes/form/FormDescriptor'
import { findItem, getFilter } from '@/store/utility_functions'
import SmsLog from '@/classes/case/SmsLog'
import EmailMessage from '@/classes/case/Message'

const DEFAULT_SORT = 'first_name,last_name'
const LOG_SORT = '-created'

const state = {
  smsLogs: [],
  emailMessages: [],
  customers: [],
  invoiceCustomerFormDescriptors: [],
  invoiceCustomerFormDatas: [],
  sendSmsFormDescriptor: [],
  sendSmsFormData: [],
  messageFormDescriptor: [],
  messageFormData: []
}

const getters = {
  getCustomerForId: state => (customerId) => {
    return findItem(state.customers, customerId)
  }
}

const mutations = {
  setStateCustomers: (state, customers) => {
    state.customers = customers
  },
  addToStateCustomer: (state, customer) => {
    state.customers.push(customer)
  },
  updateStateCustomers: (state, data) => {
    data.current.updateCurrentCustomer(data.updated)
  },
  setStateCustomerInvoiceCustomerFormDescriptors: (state, data) => {
    state.invoiceCustomerFormDescriptors = FormDescriptor.asFormDescriptors(data)
  },
  setStateCustomerInvoiceCustomerFormDatas: (state, data) => {
    state.invoiceCustomerFormDatas = FormData.initiateFormDatas(state.invoiceCustomerFormDescriptors)
    FormData.loadInitialFormDatas(data, state.invoiceCustomerFormDatas)
    FormDescriptor.checkFormDescriptorsDependencies(state.invoiceCustomerFormDescriptors, state.invoiceCustomerFormDatas)
  },
  setStateSendSmsFormDescriptor: (state, data) => {
    state.sendSmsFormDescriptor = FormDescriptor.asFormDescriptors(data)
  },
  setStateSendSmsFormData: (state, data) => {
    state.sendSmsFormData = FormData.initiateFormDatas(state.sendSmsFormDescriptor)
    FormData.loadInitialFormDatas(data, state.sendSmsFormData)
    FormDescriptor.checkFormDescriptorsDependencies(state.sendSmsFormDescriptor, state.sendSmsFormData)
  },
  setStateMessageFormDescriptor: (state, data) => {
    state.messageFormDescriptor = FormDescriptor.asFormDescriptors(data)
  },
  setStateMessageFormData: (state, data) => {
    state.messageFormData = FormData.initiateFormDatas(state.messageFormDescriptor)
    FormData.loadInitialFormDatas(data, state.messageFormData)
    FormDescriptor.checkFormDescriptorsDependencies(state.messageFormDescriptor, state.messageFormData)
  },
  setStateSmsLogs: (state, smsLogs) => {
    state.smsLogs = smsLogs
  },
  setStateEmailMessages: (state, messages) => {
    state.emailMessages = messages
  }
}

const actions = {
  getCustomers ({ commit }, request) {
    request.ordering = request.ordering || DEFAULT_SORT
    let filter = getFilter(request)
    return customerApi.getCustomers(filter).then((response) => {
      let customers = Customer.asCustomers(response.data.results)
      commit('setStateCustomers', customers)
      return response.data
    }).catch((err) => {
      throw err
    })
  },
  getSmsLogs ({ commit }, request) {
    request.ordering = request.ordering || LOG_SORT
    let filter = getFilter(request)
    return customerApi.getSmsLogs(filter).then((response) => {
      let smsLogs = SmsLog.asSmsLogs(response.data.results)
      commit('setStateSmsLogs', smsLogs)
      return response.data
    }).catch((err) => {
      throw err
    })
  },
  getMessages ({ commit }, request) {
    request.ordering = request.ordering || LOG_SORT
    let filter = getFilter(request)
    return customerApi.getMessages(filter).then((response) => {
      let messages = EmailMessage.asMessages(response.data.results)
      commit('setStateEmailMessages', messages)
      return response.data
    }).catch((err) => {
      throw err
    })
  },
  setCustomer ({ commit, state }, customerId) {
    return customerApi.getCustomer(customerId).then((response) => {
      let customer = Customer.asCustomer(response.data)
      let existingItem = findItem(state.customers, customer.id)
      if (existingItem) {
        commit('updateStateCustomers', {
          current: existingItem,
          updated: customer
        })
        return existingItem
      } else {
        commit('addToStateCustomer', customer)
      }
      return customer
    }).catch((err) => {
      throw err
    })
  },
  setupCustomerInvoiceCustomerCreateForm ({ commit, state }, customerId) {
    return customerApi.getCustomerInvoiceCustomerCreateForm(customerId).then((response) => {
      let formData = response.data.form_data ? [response.data.form_data] : []
      commit('setStateCustomerInvoiceCustomerFormDescriptors', [response.data])
      commit('setStateCustomerInvoiceCustomerFormDatas', formData)
    }).catch((err) => {
      throw err
    })
  },
  postCustomerInvoiceCustomerCreateForm ({ commit }, requestData) {
    return customerApi.postCustomerInvoiceCustomerCreateForm(requestData.data.customerId, requestData.serializedData).then((response) => {
      return response
    }).catch((err) => {
      throw err
    })
  },
  deleteInvoiceCustomerOnCustomer ({ commit, state }, customerId) {
    return customerApi.deleteInvoiceCustomerOnCustomer(customerId).then((response) => {
      return true
    }).catch((err) => {
      throw err
    })
  },
  setCurrentInvoiceCustomerOnCustomer ({ dispatch }, requestData) {
    return customerApi.putCurrentInvoiceCustomerOnCustomer(requestData.customerId, requestData.data).then((response) => {
      return response
    }).catch((err) => {
      throw err
    })
  },
  getMessageForm ({ commit }, caseId) {
    return customerApi.getMessageForm(caseId).then((response) => {
      let formData = response.data.form_data ? [response.data.form_data] : []
      commit('setStateMessageFormDescriptor', [response.data])
      commit('setStateMessageFormData', formData)
    }).catch((err) => {
      throw err
    })
  },
  saveMessageForm ({ commit }, requestData) {
    return customerApi.putMessageForm(requestData.data.caseId, requestData.serializedData).then((response) => {
      return response
    }).catch((err) => {
      throw err
    })
  },
  setupSendSmsForm ({ commit }, customerId) {
    return customerApi.getSendSmsForm(customerId).then((response) => {
      let formData = response.data.form_data ? [response.data.form_data] : []
      commit('setStateSendSmsFormDescriptor', [response.data])
      commit('setStateSendSmsFormData', formData)
    }).catch((err) => {
      throw err
    })
  },
  saveSendSmsForm ({ commit }, requestData) {
    return customerApi.putSendSmsForm(requestData.data.customerId, requestData.serializedData).then((response) => {
      return response
    }).catch((err) => {
      throw err
    })
  },
  saveSendCaseSmsForm ({ commit }, requestData) {
    return customerApi.putSendCaseSmsForm(requestData.data.customerId, requestData.data.caseId, requestData.serializedData).then((response) => {
      return response
    }).catch((err) => {
      throw err
    })
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}
